import { ResponsiveSheet } from '@/components/ResponsiveSheet';
import { COLUMN_BY_STATUS } from '@/features/board/constants';
import { Clock } from 'lucide-react';
import { Markdown } from '@/components/Markdown';
import { Skeleton, SkeletonGroup } from '@/components/Skeleton';
import { Avatar } from '@/app/layouts/Sidebar';
import { useTaskEvents } from './useTaskEvents';
import { useMarkRead } from './useMarkRead';
import type { Task, TaskEvent } from '@/types/api';

/**
 * Read-only, by design.
 *
 * There is no control here that moves a card. People read the story and reply on
 * Telegram; the desks act on it. The board is the single reading place, which is why
 * the context log gets the room rather than a set of buttons.
 */

const EVENT_LABEL: Record<string, string> = {
  created: 'Assigned',
  note: '',
  status_changed: 'Moved',
  reassigned: 'Reassigned',
  priority_changed: 'Priority changed',
  due_date_changed: 'Reminder changed',
  tag_changed: 'Tag changed',
  archived: 'Archived',
  restored: 'Restored',
};

function actorName(event: TaskEvent): string {
  if (event.actorType === 'agent') return event.actorDeskId ? `${event.actorDeskId} desk` : 'the agent';
  if (event.actorType === 'admin') return `${event.actorName ?? 'an admin'} (override)`;
  if (event.actorType === 'system') return 'the system';
  return event.actorName ?? 'someone';
}

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

export function TaskDetailSheet({ task, onClose }: { task: Task | null; onClose: () => void }) {
  // The board copy already carries every field, so there is nothing to fetch — opening
  // the card only needs to TELL the server it was opened.
  useMarkRead(task?.id ?? null);
  const shown = task;
  const events = useTaskEvents(task?.id ?? null);
  // Counts notes only. The list also carries what the desks CHANGED — moves, retags —
  // but those are a record of the card, not something someone wrote.
  const noteCount = events.data?.items.filter((e) => e.eventType === 'note').length ?? 0;
  const column = shown ? COLUMN_BY_STATUS.get(shown.status) : undefined;

  return (
    <ResponsiveSheet open={task !== null} onClose={onClose} title={shown?.title ?? ''}>
      {shown && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-1.5">
            <span
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium"
              style={{ color: column?.colorVar, background: column?.softVar }}
            >
              {column && <column.icon size={12} />}
              {column?.label}
            </span>
            <span
              className="rounded-md px-1.5 py-0.5 font-mono text-[11px]"
              style={{ background: 'var(--surface-sunken)', color: 'var(--text-muted)' }}
            >
              {shown.reference}
            </span>
            {shown.tag && (
              <span
                className="rounded-md px-1.5 py-0.5 text-[11px]"
                style={{ background: 'var(--surface-sunken)', color: 'var(--text-muted)' }}
              >
                {shown.tag}
              </span>
            )}
            <span
              className="rounded-md px-1.5 py-0.5 text-[11px] font-medium capitalize"
              style={{
                color: `var(--color-priority-${shown.priority})`,
                background: `color-mix(in oklch, var(--color-priority-${shown.priority}) 15%, transparent)`,
              }}
            >
              {shown.priority}
            </span>
          </div>

          <div
            className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px]"
            style={{ color: 'var(--text-muted)' }}
          >
            {shown.assignee && (
              <span className="flex items-center gap-1.5">
                <Avatar
                  name={shown.assignee.name || shown.assignee.email}
                  url={shown.assignee.avatarUrl}
                  size={18}
                />
                {shown.assignee.name || shown.assignee.email}
              </span>
            )}
            {shown.remindAt && (
              <span className="flex items-center gap-1.5">
                <Clock size={13} />
                {when(shown.remindAt)}
              </span>
            )}
          </div>

          {shown.status === 'waiting' && shown.waitingOn && (
            <p
              className="rounded-lg border px-3 py-2 text-xs"
              style={{ borderColor: 'var(--color-status-waiting)', color: 'var(--text-muted)' }}
            >
              Held on <span style={{ color: 'var(--text)' }}>{shown.waitingOn}</span>
            </p>
          )}

          {shown.cancelledReason && (
            <p className="rounded-lg px-3 py-2 text-xs" style={{ background: 'var(--surface-sunken)' }}>
              Cancelled: {shown.cancelledReason}
            </p>
          )}

          {/*
            The sheet is wide so tables and code have room; the prose is not, because a
            56rem measure is a genuinely hard line length to read. Tables and pre blocks
            still overflow this to the full width — .md gives them their own scroller.
          */}
          {shown.description && (
            <div className="max-w-[46rem]">
              <Markdown>{shown.description}</Markdown>
            </div>
          )}

          {/*
            A real section header with a rule above it, not a faint caption.
            "Context" named the concept rather than the content, and set in 10px grey it
            read as a label on the description above rather than the start of something
            new — so the notes looked like a continuation of the brief.
          */}
          <section className="border-t pt-4" style={{ borderColor: 'var(--border)' }}>
            <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
              Notes
              {noteCount > 0 && (
                <span
                  className="rounded-md px-1.5 py-0.5 text-[11px] font-medium tabular-nums"
                  style={{ background: 'var(--surface-sunken)', color: 'var(--text-muted)' }}
                >
                  {noteCount}
                </span>
              )}
            </h3>

            {events.isPending && (
              <SkeletonGroup
                label="Loading notes"
                className="flex flex-col gap-3 border-l pl-4"
              >
                {[0, 1, 2].map((i) => (
                  <div key={i} className="flex flex-col gap-1.5">
                    <Skeleton width={i === 1 ? '85%' : '65%'} height={11} />
                    <Skeleton width={110} height={9} />
                  </div>
                ))}
              </SkeletonGroup>
            )}

            {/*
              Oldest first: this reads as a story, not a changelog. The rail down the
              left is what makes it read as one thread rather than a list of rows.
            */}
            <ol
              className="flex flex-col gap-3 border-l pl-4"
              style={{ borderColor: 'var(--border)' }}
            >
              {events.data?.items.map((event) => (
                <li key={event.id} className="relative text-xs leading-relaxed">
                  <span
                    aria-hidden
                    className="absolute -left-[21px] top-1.5 size-2 rounded-full ring-2"
                    style={{
                      background: event.eventType === 'note' ? 'var(--accent)' : 'var(--border-strong)',
                      // The ring punches the rail out behind the dot, so the line reads
                      // as passing behind rather than through it.
                      // @ts-expect-error -- CSS custom property for the ring colour
                      '--tw-ring-color': 'var(--surface)',
                    }}
                  />

                  {event.eventType === 'note' ? (
                    <>
                      <Markdown>{event.body ?? ''}</Markdown>
                      <p className="mt-1" style={{ color: 'var(--text-faint)' }}>
                        {actorName(event)} · {when(event.createdAt)}
                      </p>
                    </>
                  ) : (
                    <p style={{ color: 'var(--text-muted)' }}>
                      <span className="font-medium" style={{ color: 'var(--text)' }}>
                        {EVENT_LABEL[event.eventType] ?? event.eventType}
                      </span>
                      {event.fromStatus && event.toStatus && ` ${event.fromStatus} → ${event.toStatus}`}
                      {' by '}{actorName(event)} · {when(event.createdAt)}
                      {event.body && (
                        <span className="mt-1 block rounded-md px-2 py-1" style={{ background: 'var(--surface-sunken)' }}>
                          {event.body}
                        </span>
                      )}
                    </p>
                  )}
                </li>
              ))}
            </ol>
          </section>
        </div>
      )}
    </ResponsiveSheet>
  );
}
